import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { seedData } from '../lib/api';

export const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const userData = await login(email, password);

            // Seed demo data on first login
            try {
                await seedData();
            } catch (err) {
                // Data might already be seeded
            }

            // Force password change for temp passwords, otherwise normal flow
            if (userData?.must_change_password) {
                navigate('/change-password');
            } else if (userData?.role === 'client') {
                navigate('/dashboard');
            } else {
                navigate('/start-trial');
            }

        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail) ? (detail[0]?.msg || detail[0]) : detail;
            setError(msg || 'Invalid email or password. Check for typos and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-split" data-testid="login-page">
            {/* Left side - Hero Image */}
            <div 
                className="hidden lg:flex items-center justify-center bg-slate-900 relative overflow-hidden"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&q=85)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-slate-900/70" />
                <div className="relative z-10 text-white p-12 max-w-lg">
                    <Shield className="w-12 h-12 mb-6" />
                    <h1 className="font-heading text-4xl font-bold mb-4 tracking-tight">
                        Secure Your Business Infrastructure
                    </h1>
                    <p className="text-lg text-slate-300 leading-relaxed">
                        Real-time visibility into your devices, users, and network security. 
                        Simplified IT management for small businesses.
                    </p>
                </div>
            </div>

            {/* Right side - Login Form */}
            <div className="flex items-center justify-center p-8 bg-background">
                <div className="w-full max-w-md">

                    {/* Mobile Logo */}
                    <div className="flex items-center gap-2 mb-8 lg:hidden">
                        <Shield className="w-8 h-8 text-primary" />
                        <span className="font-heading text-2xl font-bold tracking-tight">SecureSight</span>
                    </div>

                    <Card className="border-border shadow-sm">
                        <CardHeader className="space-y-1 pb-4">
                            <CardTitle className="font-heading text-2xl font-bold tracking-tight">
                                Welcome back
                            </CardTitle>
                            <CardDescription className="text-muted-foreground">
                                Sign in to your account to continue
                            </CardDescription>
                        </CardHeader>

                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">

                                {error && (
                                    <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                                        <AlertCircle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="name@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>

                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="pr-10"
                                        />

                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <Button 
                                    type="submit"
                                    className="w-full"
                                    disabled={loading}
                                >
                                    {loading ? 'Signing in...' : 'Sign in'}
                                </Button>

                            </form>

                            <div className="mt-6 text-center text-sm">
                                <span className="text-muted-foreground">
                                    Don't have an account?
                                </span>

                                <Link 
                                    to="/register"
                                    className="text-primary font-medium hover:underline ml-1"
                                >
                                    Create account
                                </Link>
                            </div>

                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};
